import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout, StatusBadge } from "@/pages/Dashboard";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, CalendarIcon, Download, Save, ArrowLeft, Send, CheckCircle, Mail, Copy, ExternalLink, Link2 } from "lucide-react";
import { format, parseISO } from "date-fns";

const PAYMENT_METHODS = [
  "Bank Transfer",
  "Cash",
  "PayPal",
  "Stripe",
  "Credit Card",
  "Check",
  "Other"
];

const EditInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  
  const [formData, setFormData] = useState(null);
  const [paymentData, setPaymentData] = useState({
    payment_date: new Date(),
    payment_method: "",
    payment_notes: ""
  });

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await axios.get(`${API}/invoices/${id}`, { withCredentials: true });
        const invoice = res.data;
        setFormData({
          ...invoice,
          invoice_date: parseISO(invoice.invoice_date),
          due_date: parseISO(invoice.due_date)
        });
      } catch (error) {
        console.error("Error fetching invoice:", error);
        toast.error("Failed to load invoice");
        navigate('/invoices');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id, navigate]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, unit_price: 0, total_price: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  // Calculate totals
  const subtotal = formData?.items?.reduce((sum, item) => {
    return sum + (Number(item.quantity) * Number(item.unit_price));
  }, 0) || 0;
  const taxAmount = subtotal * (Number(formData?.tax_percentage || 0) / 100);
  const total = subtotal + taxAmount;

  const handleSave = async () => {
    if (!formData.business_name || !formData.client_name || !formData.client_email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        invoice_date: format(formData.invoice_date, "yyyy-MM-dd"),
        due_date: format(formData.due_date, "yyyy-MM-dd"),
        items: formData.items.map(item => ({
          description: item.description,
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_price)
        })),
        tax_percentage: Number(formData.tax_percentage)
      };

      await axios.put(`${API}/invoices/${id}`, payload, { withCredentials: true });
      toast.success("Invoice updated successfully!");
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to update invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/invoices/${id}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${formData.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice downloaded!");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download invoice");
    } finally {
      setDownloading(false);
    }
  };

  const handleSendInvoice = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/invoices/${id}/send`, {}, { withCredentials: true });
      toast.success(`Invoice sent to ${formData.client_email}`);
      
      // Update local state
      setFormData({
        ...formData,
        sent_at: new Date().toISOString(),
        email_status: "sent"
      });
    } catch (error) {
      console.error("Error sending invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to send invoice");
    } finally {
      setSending(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!paymentData.payment_method) {
      toast.error("Please select a payment method");
      return;
    }

    setMarkingPaid(true);
    try {
      await axios.post(`${API}/invoices/${id}/mark-paid`, {
        payment_date: format(paymentData.payment_date, "yyyy-MM-dd"),
        payment_method: paymentData.payment_method,
        payment_notes: paymentData.payment_notes
      }, { withCredentials: true });
      
      toast.success("Invoice marked as paid!");
      setPaymentDialogOpen(false);
      
      // Update local state
      setFormData({
        ...formData,
        status: "Paid",
        payment_date: format(paymentData.payment_date, "yyyy-MM-dd"),
        payment_method: paymentData.payment_method,
        payment_notes: paymentData.payment_notes
      });
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      toast.error(error.response?.data?.detail || "Failed to update invoice");
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "Paid" && formData.status !== "Paid") {
      setPaymentDialogOpen(true);
      return;
    }
    
    setFormData({ ...formData, status: newStatus });
  };

  const copyShareableLink = () => {
    const link = `${window.location.origin}/invoice/view/${id}`;
    navigator.clipboard.writeText(link);
    toast.success("Shareable link copied to clipboard!");
  };

  const openPublicView = () => {
    window.open(`/invoice/view/${id}`, '_blank');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 space-y-6">
              <Card className="bg-white">
                <CardContent className="p-6 space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-24 w-full" />
                </CardContent>
              </Card>
            </div>
            <div className="lg:col-span-2">
              <Skeleton className="h-96 w-full" />
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!formData) return null;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="edit-invoice-page">
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/invoices')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Invoice #{formData.invoice_number}
              </h1>
              <StatusBadge status={formData.status} />
            </div>
            <p className="text-muted-foreground">
              {formData.sent_at ? (
                <span className="flex items-center gap-1 text-green-600">
                  <Mail className="w-3 h-3" /> Sent on {format(parseISO(formData.sent_at), "MMM dd, yyyy")}
                </span>
              ) : (
                "Not sent yet"
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select 
              value={formData.status} 
              onValueChange={handleStatusChange}
            >
              <SelectTrigger className="w-32" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Form Section */}
          <div className="lg:col-span-3 space-y-6">
            {/* Payment Info (if paid) */}
            {formData.status === "Paid" && formData.payment_date && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Payment Received</p>
                      <p className="text-sm text-green-600">
                        {formData.payment_method} on {formData.payment_date}
                        {formData.payment_notes && ` - ${formData.payment_notes}`}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Business Info */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-base">Your Business Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    data-testid="edit-business-name-input"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="business_address">Business Address</Label>
                  <Textarea
                    id="business_address"
                    data-testid="edit-business-address-input"
                    value={formData.business_address}
                    onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Client Info */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-base">Client Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="client_name">Client Name *</Label>
                  <Input
                    id="client_name"
                    data-testid="edit-client-name-input"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="client_email">Client Email *</Label>
                  <Input
                    id="client_email"
                    type="email"
                    data-testid="edit-client-email-input"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="client_address">Client Address</Label>
                  <Textarea
                    id="client_address"
                    data-testid="edit-client-address-input"
                    value={formData.client_address}
                    onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Invoice Details */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-base">Invoice Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="invoice_number">Invoice Number</Label>
                    <Input
                      id="invoice_number"
                      data-testid="edit-invoice-number-input"
                      value={formData.invoice_number}
                      onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Invoice Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="edit-invoice-date-btn">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.invoice_date, "MMM dd, yyyy")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.invoice_date}
                          onSelect={(date) => date && setFormData({ ...formData, invoice_date: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Due Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="edit-due-date-btn">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.due_date, "MMM dd, yyyy")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.due_date}
                          onSelect={(date) => date && setFormData({ ...formData, due_date: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Invoice Items */}
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Invoice Items</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="edit-add-item-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground uppercase">
                    <div className="col-span-6">Description</div>
                    <div className="col-span-2">Qty</div>
                    <div className="col-span-3">Rate</div>
                    <div className="col-span-1"></div>
                  </div>
                  
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center" data-testid={`edit-invoice-item-${index}`}>
                      <div className="col-span-6">
                        <Input
                          placeholder="Item description"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          data-testid={`edit-item-description-${index}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          data-testid={`edit-item-quantity-${index}`}
                        />
                      </div>
                      <div className="col-span-3">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="pl-7"
                            value={item.unit_price}
                            onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                            data-testid={`edit-item-price-${index}`}
                          />
                        </div>
                      </div>
                      <div className="col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(index)}
                          disabled={formData.items.length === 1}
                          data-testid={`edit-remove-item-${index}`}
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t border-border">
                  <div className="flex items-center justify-between max-w-xs ml-auto">
                    <Label htmlFor="tax">Tax (%)</Label>
                    <Input
                      id="tax"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      className="w-24 text-right"
                      value={formData.tax_percentage}
                      onChange={(e) => setFormData({ ...formData, tax_percentage: e.target.value })}
                      data-testid="edit-tax-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-base">Additional Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    data-testid="edit-notes-input"
                    value={formData.notes || ""}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="payment_instructions">Payment Instructions</Label>
                  <Textarea
                    id="payment_instructions"
                    data-testid="edit-payment-instructions-input"
                    value={formData.payment_instructions || ""}
                    onChange={(e) => setFormData({ ...formData, payment_instructions: e.target.value })}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-2">
            <div className="sticky top-24">
              <Card className="bg-white invoice-paper">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground uppercase tracking-wider">Invoice Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 text-sm">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xl font-bold text-primary">INVOICE</p>
                        <p className="text-xs text-muted-foreground">#{formData.invoice_number}</p>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <p>{format(formData.invoice_date, "MMM dd, yyyy")}</p>
                        <p>Due: {format(formData.due_date, "MMM dd, yyyy")}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <p className="font-semibold text-muted-foreground mb-1">FROM</p>
                        <p className="font-medium">{formData.business_name}</p>
                        <p className="text-muted-foreground whitespace-pre-line">{formData.business_address}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-muted-foreground mb-1">TO</p>
                        <p className="font-medium">{formData.client_name}</p>
                        <p className="text-muted-foreground">{formData.client_email}</p>
                      </div>
                    </div>

                    <div className="border-t border-b border-border py-3">
                      {formData.items.map((item, i) => (
                        <div key={i} className="flex justify-between py-1">
                          <span className="truncate flex-1 mr-2">{item.description || "Item"}</span>
                          <span className="font-mono text-right">
                            ${(Number(item.quantity) * Number(item.unit_price)).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-1 text-right">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="font-mono">${subtotal.toFixed(2)}</span>
                      </div>
                      {Number(formData.tax_percentage) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tax ({formData.tax_percentage}%)</span>
                          <span className="font-mono">${taxAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold text-primary pt-2 border-t border-border">
                        <span>Total</span>
                        <span className="font-mono">${total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="mt-4 space-y-3">
                <Button 
                  onClick={handleSave}
                  className="w-full bg-primary hover:bg-primary/90 btn-hover-lift"
                  disabled={saving}
                  data-testid="save-invoice-btn"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline"
                    onClick={handleSendInvoice}
                    disabled={sending}
                    data-testid="send-invoice-btn"
                  >
                    {sending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={handleDownloadPDF}
                    disabled={downloading}
                    data-testid="download-pdf-btn"
                  >
                    {downloading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                        ...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        PDF
                      </>
                    )}
                  </Button>
                </div>

                {formData.status !== "Paid" && (
                  <Button 
                    variant="outline"
                    onClick={() => setPaymentDialogOpen(true)}
                    className="w-full border-green-200 text-green-700 hover:bg-green-50"
                    data-testid="mark-paid-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Paid
                  </Button>
                )}

                {/* Shareable Link Section */}
                <Card className="bg-muted/50 border-dashed">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Shareable Link</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      Share this link with your client to view the invoice online
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={copyShareableLink}
                        data-testid="copy-link-btn"
                      >
                        <Copy className="w-3 h-3 mr-2" />
                        Copy Link
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={openPublicView}
                        data-testid="open-public-view-btn"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
              <DialogDescription>
                Enter the payment details for this invoice
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Payment Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="payment-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(paymentData.payment_date, "MMM dd, yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={paymentData.payment_date}
                      onSelect={(date) => date && setPaymentData({ ...paymentData, payment_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Payment Method *</Label>
                <Select 
                  value={paymentData.payment_method}
                  onValueChange={(value) => setPaymentData({ ...paymentData, payment_method: value })}
                >
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method} value={method}>{method}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={paymentData.payment_notes}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_notes: e.target.value })}
                  placeholder="Reference number, transaction ID, etc."
                  rows={2}
                  data-testid="payment-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleMarkAsPaid}
                disabled={markingPaid}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-payment-btn"
              >
                {markingPaid ? "Saving..." : "Confirm Payment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default EditInvoice;
